import React, {
  createContext,
  useState,
  useCallback,
  useContext,
  ReactNode,
} from "react";
import api from "../../api/api"; // Axios instance for API calls
import { PaginationInfo } from "../../types/api";

interface WorkLocationsContextType {
  locations: any[];
  /** Legacy alias read by older consumers via the `in` operator; always undefined in practice. */
  workLocations?: any[];
  pagination: PaginationInfo | null;
  loading: boolean;
  fetchLocations: (page?: number, perPage?: number) => Promise<void>;
  /** Legacy alias referenced by older consumers; not implemented by the provider (no-op at runtime). */
  fetchWorkLocations?: (page?: number, perPage?: number) => Promise<void>;
  addLocation: (location: any) => Promise<void>;
  editLocation: (location: any) => Promise<void>;
  deleteLocation: (id: number | string) => Promise<void>;
}

export const WorkLocationsContext = createContext<
  WorkLocationsContextType | undefined
>(undefined);

interface WorkLocationsProviderProps {
  children: ReactNode;
}

export const useWorkLocationsContext = (): WorkLocationsContextType => {
  const context = useContext(WorkLocationsContext);
  if (!context) {
    throw new Error(
      "useWorkLocationsContext must be used within WorkLocationsProvider",
    );
  }
  return context;
};

const WorkLocationsProvider: React.FC<WorkLocationsProviderProps> = ({
  children,
}) => {
  const [locations, setLocations] = useState<any[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  // Fetch all work locations with pagination
  const fetchLocations = useCallback(async (page: number = 1, perPage: number = 15) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: perPage.toString(),
      });
      const response = await api.get(`/work-locations?${params.toString()}`);
      
      // Handle paginated response structure: { success: true, data: { data: [...], pagination: {...} } }
      let locationsData = [];
      let paginationData = null;
      const responseData = response.data;
      
      if (responseData?.success) {
        if (responseData.data?.data && Array.isArray(responseData.data.data)) {
          locationsData = responseData.data.data;
          paginationData = responseData.data.pagination || responseData.pagination;
        } else if (responseData.data && Array.isArray(responseData.data)) {
          locationsData = responseData.data;
          paginationData = responseData.pagination;
        } else if (Array.isArray(responseData)) {
          locationsData = responseData;
        }
      } else if (Array.isArray(responseData?.data)) {
        locationsData = responseData.data;
        paginationData = responseData.pagination;
      } else if (Array.isArray(responseData)) {
        locationsData = responseData;
      }
      
      setLocations(locationsData);
      setPagination(paginationData);
    } catch (error) {
      console.error("Failed to fetch work locations:", error);
      setLocations([]);
      setPagination(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Add a new work location
  const addLocation = async (location: any): Promise<void> => {
    try {
      const response = await api.post("/work-locations", location);
      const newLocation = response.data.data || response.data;
      setLocations((prev) => [...prev, newLocation]);
    } catch (error) {
      console.error("Failed to add work location:", error);
      throw error;
    }
  };

  // Edit an existing work location
  const editLocation = async (location: any): Promise<void> => {
    try {
      const response = await api.put(`/work-locations/${location.id}`, location);
      const updatedLocation = response.data.data || response.data;
      setLocations((prev) =>
        prev.map((item) => (item.id === location.id ? updatedLocation : item))
      );
    } catch (error) {
      console.error("Failed to edit work location:", error);
      throw error;
    }
  };

  // Delete a work location
  const deleteLocation = async (id: number | string): Promise<void> => {
    try {
      await api.delete(`/work-locations/${id}`);
      setLocations((prev) => prev.filter((item) => item.id !== id));
    } catch (error) {
      console.error("Failed to delete work location:", error);
      throw error;
    }
  };

  // REMOVED auto-fetch useEffect - components will call fetchLocations explicitly with pagination

  return (
    <WorkLocationsContext.Provider
      value={{
        locations,
        pagination,
        loading,
        fetchLocations,
        addLocation,
        editLocation,
        deleteLocation,
      }}
    >
      {children}
    </WorkLocationsContext.Provider>
  );
};

export default WorkLocationsProvider;
